import React, { ChangeEvent, FormEvent, useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Link } from '~/lib/react-router-dom';

import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { generateTripsFromCart } from '~/services/TripPlannerService';

type GeneratedTrip = {
  id: string;
  name: string;
  cartId: string;
};

export default function TripPlannerGenerateTripsFromCart() {
  const [cartId, setCartId] = useState<string>('');
  const [generatedTrips, setGeneratedTrips] = useState<Array<GeneratedTrip>>([]);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleCartIdChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setCartId(event.target.value);
  }, []);

  const handleGenerateTrip = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      if (!cartId.trim()) {
        enqueueSnackbar('Please enter a cart ID', { variant: 'warning' });
        return;
      }

      setIsGenerating(true);

      try {
        const response = await generateTripsFromCart(cartId.trim());
        if (response.status === 200) {
          const newTrip: GeneratedTrip = {
            id: response.result.result.id,
            name: response.result.result.name || `Trip from Cart ${cartId}`,
            cartId: cartId.trim(),
          };

          setGeneratedTrips((prev) => [newTrip, ...prev]);
          setCartId('');
          enqueueSnackbar('Trip generated successfully', { variant: 'success' });
        } else {
          enqueueSnackbar('Failed to generate trip', { variant: 'error' });
        }
      } catch (error) {
        console.error('Failed to generate trip:', error);
        enqueueSnackbar('Failed to generate trip', { variant: 'error' });
      } finally {
        setIsGenerating(false);
      }
    },
    [cartId, enqueueSnackbar],
  );

  return (
    <Container>
      <Typography variant="h6" gutterBottom>
        Generate Trips from Cart
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Enter Cart ID to Generate Trip:
        </Typography>
        <Box component="form" onSubmit={handleGenerateTrip}>
          <Stack spacing={2}>
            <TextField
              required
              fullWidth
              value={cartId}
              onChange={handleCartIdChange}
              placeholder="Enter Cart ID"
              disabled={isGenerating}
              label="Cart ID"
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isGenerating || !cartId.trim()}
                startIcon={isGenerating ? <CircularProgress size={20} /> : null}
              >
                {isGenerating ? 'Generating...' : 'Generate Trip'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Generated Trips
      </Typography>

      {generatedTrips.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No trips generated yet. Enter a cart ID above to generate your first trip.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Trip Name</TableCell>
                <TableCell>Trip ID</TableCell>
                <TableCell>Source Cart ID</TableCell>
                <TableCell align="center">Link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {generatedTrips.map((trip) => (
                <TableRow key={trip.id}>
                  <TableCell>{trip.name}</TableCell>
                  <TableCell>{trip.id}</TableCell>
                  <TableCell>{trip.cartId}</TableCell>
                  <TableCell align="center">
                    <Link to={`/trip-planner/classic/trip/${trip.id}`}>View Trip</Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}
